import { CircleCheck, CircleX, History, ListFilter, TriangleAlert } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  cn,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  EmptyState,
  formatInt,
  GROUP_INDENT,
  GroupChevron,
  Panel,
  plural,
  Pressable,
  Segmented,
  Select,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@orgus/ui';
import type { Violation } from '@orgus/protocol';
import { SheetFilter } from '../audit-ops/AuditExplorer.tsx';
import { violationFeed } from './feed.ts';
import type { FeedGroup } from './feed.ts';
import type { RepoRow } from './model.ts';
import { ViolationActions } from './ViolationActions.tsx';

/** Violations by repo inside one rule, each repo with its own actions. */
function RuleBody({ g }: { g: FeedGroup }) {
  const byRepo = useMemo(() => {
    const m = new Map<string, { projectId: string; projectName: string; violations: Violation[] }>();
    for (const e of g.entries) {
      let x = m.get(e.projectId);
      if (!x) m.set(e.projectId, (x = { projectId: e.projectId, projectName: e.projectName, violations: [] }));
      x.violations.push(e.violation);
    }
    return [...m.values()];
  }, [g]);
  return (
    // One group step in from the header padding, under the rule's icon (GROUP_INDENT).
    <div className="px-4 pb-3">
      <div className={cn('flex flex-col gap-3', GROUP_INDENT)}>
        <p className="text-sm text-fg-muted">
          {g.info.why} <span className="text-fg">Fix: {g.info.fix}</span>
        </p>
        {byRepo.map((r) => (
          <div key={r.projectId} className="overflow-hidden rounded-md border border-border-subtle bg-bg">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-1.5">
              <span className="text-sm font-medium text-fg">
                {r.projectName} <span className="font-normal text-fg-subtle">{plural(r.violations.length, 'violation')}</span>
              </span>
              <ViolationActions target={r} compact />
            </div>
            <ul>
              {r.violations.map((v, i) => (
                <li key={`${v.path}:${v.line ?? 0}:${i}`} className="flex min-w-0 flex-col gap-0.5 border-b border-border-subtle px-3 py-1.5 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3">
                  <span className="min-w-0 shrink-0 font-mono text-caption break-all text-fg sm:max-w-1/2 sm:truncate">
                    {v.path}
                    {v.line ? <span className="text-fg-subtle">:{v.line}</span> : null}
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-fg-muted sm:truncate">{v.message}</span>
                  {v.debt && (
                    <Badge size="sm" variant="outline" icon={<History />}>
                      Debt
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {g.hidden > 0 && <p className="text-sm text-fg-subtle">{plural(g.hidden, 'more violation')} counted but not listed; reports carry the first 50 per rule.</p>}
      </div>
    </div>
  );
}

/** Every violation across the fleet, grouped by rule, with send back, waive and open task. */
export function ViolationFeed({ rows, projectId, onProject }: { rows: RepoRow[]; projectId: string | null; onProject: (id: string | null) => void }) {
  const [severity, setSeverity] = useState<'all' | 'error' | 'warn'>('all');
  const [debt, setDebt] = useState<'new' | 'all'>('new');
  const groups = useMemo(() => violationFeed(rows, { projectId, severity, debt: debt === 'all' }), [rows, projectId, severity, debt]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (rule: string, i: number) => open[rule] ?? i === 0;
  const anyDebt = rows.some((r) => r.report?.violations.some((v) => v.debt));
  const checked = rows.filter((r) => r.report && !r.report.error).length;
  /** On a phone the three filters open in a bottom sheet behind one Filters button (the Audit pattern). */
  const [sheet, setSheet] = useState(false);
  const active = (projectId ? 1 : 0) + (severity !== 'all' ? 1 : 0) + (anyDebt && debt === 'all' ? 1 : 0);
  const shown = groups.reduce((n, g) => n + g.errors + g.warns, 0);
  const SEVERITY_TEXT = { all: 'All', error: 'Errors', warn: 'Warnings' } as const;
  const DEBT_TEXT = { new: 'New only', all: 'With debt' } as const;

  return (
    <>
      <Panel
        title="Violations by rule"
        sub={
          groups.length ? (
            <>
              {/* A phone keeps only the quiet count, so the caption never cuts beside the Filters button. */}
              <span className="sm:hidden">{formatInt(groups.length)}</span>
              <span className="max-sm:hidden">{`${plural(groups.length, 'rule')} across ${plural(checked, 'repo')}`}</span>
            </>
          ) : undefined
        }
        icon={<ListFilter />}
        flush
        actions={
          <>
            <Button size="sm" variant={active ? 'secondary' : 'ghost'} icon={<ListFilter />} aria-haspopup="dialog" onClick={() => setSheet(true)} className="sm:hidden">
              {active ? `Filters ${formatInt(active)}` : 'Filters'}
            </Button>
            <div className="hidden flex-wrap items-center gap-2 sm:flex">
              <Select
                size="sm"
                aria-label="Repo"
                value={projectId ?? ''}
                onValueChange={(v) => onProject(v || null)}
                options={[{ value: '', label: 'Every repo' }, ...rows.map((r) => ({ value: r.projectId, label: r.name }))]}
              />
              <Segmented
                size="sm"
                label="Severity"
                value={severity}
                onChange={setSeverity}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'error', label: 'Errors', icon: <CircleX /> },
                  { value: 'warn', label: 'Warnings', icon: <TriangleAlert /> },
                ]}
              />
              {anyDebt && (
                <Segmented
                  size="sm"
                  label="Ratchet"
                  value={debt}
                  onChange={setDebt}
                  options={[
                    { value: 'new', label: 'New only' },
                    { value: 'all', label: 'With debt', icon: <History /> },
                  ]}
                />
              )}
            </div>
          </>
        }
      >
        {groups.length ? (
          <ul>
            {groups.map((g, i) => {
              const isErr = g.errors > 0;
              const Icon = isErr ? CircleX : TriangleAlert;
              return (
                <li key={g.rule} className="border-b border-border-subtle last:border-b-0">
                  <Pressable onClick={() => setOpen((o) => ({ ...o, [g.rule]: !isOpen(g.rule, i) }))} aria-expanded={isOpen(g.rule, i)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-hover">
                    <span className="flex shrink-0 items-center gap-2">
                      <GroupChevron open={isOpen(g.rule, i)} />
                      <Icon className={cn('size-4 shrink-0', isErr ? 'text-status-danger' : 'text-status-attention')} aria-hidden />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="line-clamp-2 text-ui font-medium text-fg sm:line-clamp-none sm:truncate">{g.info.title}</span>
                      <span className="truncate font-mono text-caption text-fg-subtle">{g.rule}</span>
                    </span>
                    <span className="hidden text-caption text-fg-subtle sm:inline">{plural(g.repos, 'repo')}</span>
                    <span className={cn('shrink-0 text-right text-ui font-medium num sm:w-24', isErr ? 'text-status-danger' : 'text-status-attention')}>{isErr ? plural(g.errors, 'error') : plural(g.warns, 'warning')}</span>
                  </Pressable>
                  {isOpen(g.rule, i) && <RuleBody g={g} />}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState
            icon={<CircleCheck />}
            title={checked ? 'No violations here' : 'No repo has been checked yet'}
            description={
              checked
                ? debt === 'new' && anyDebt
                  ? 'Nothing new breaks a rule. Tracked debt is hidden; switch to With debt to see it.'
                  : 'Every checked repo follows its rules for this filter.'
                : 'Scores and violations appear here after the first check of each repo.'
            }
          />
        )}
      </Panel>
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription className="sr-only">Narrow the violations</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col py-2">
            <SheetFilter name="Repo" value={rows.find((r) => r.projectId === projectId)?.name ?? 'Every repo'}>
              <DropdownMenuRadioGroup value={projectId ?? ''} onValueChange={(v) => onProject(v || null)}>
                <DropdownMenuRadioItem value="">Every repo</DropdownMenuRadioItem>
                {rows.map((r) => (
                  <DropdownMenuRadioItem key={r.projectId} value={r.projectId}>
                    {r.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </SheetFilter>
            <SheetFilter name="Severity" value={SEVERITY_TEXT[severity]}>
              <DropdownMenuRadioGroup value={severity} onValueChange={(v) => setSeverity(v as typeof severity)}>
                {(['all', 'error', 'warn'] as const).map((v) => (
                  <DropdownMenuRadioItem key={v} value={v}>
                    {SEVERITY_TEXT[v]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </SheetFilter>
            {anyDebt && (
              <SheetFilter name="Ratchet" value={DEBT_TEXT[debt]}>
                <DropdownMenuRadioGroup value={debt} onValueChange={(v) => setDebt(v as typeof debt)}>
                  {(['new', 'all'] as const).map((v) => (
                    <DropdownMenuRadioItem key={v} value={v}>
                      {DEBT_TEXT[v]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </SheetFilter>
            )}
          </div>
          <SheetFooter>
            {active > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  onProject(null);
                  setSeverity('all');
                  setDebt('new');
                }}
              >
                Clear filters
              </Button>
            )}
            <Button variant="primary" onClick={() => setSheet(false)}>
              {`Show ${plural(shown, 'violation')}`}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
